import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { logLeadSubmitted } from 'redux-implementations/leadSubmissions';
import { signedIn as sessionSignedIn } from 'redux-implementations/session';
import { getGeoLocation } from 'redux-implementations/selectors';
import { AppDispatch } from 'redux-implementations/makeStore';

import responseSuccessEvent from 'lib/Tracking/LeadForm/ResponseSuccessEvent';
import responseFailureEvent from 'lib/Tracking/LeadForm/ResponseFailureEvent';
import { saveMostRecentLeadSubmission } from 'lib/mostRecentLeadSubmission';
import requestSentEvent from 'lib/Tracking/LeadForm/RequestSentEvent';
import { autolistFetch, withOptionalAuth } from 'lib/AutolistAPI';
import { ensureSignedIn } from 'lib/UseCases/signInAsGuest';
import { isSignedIn } from 'lib/Auth';
import LeadParams, { ContactInfo } from 'lib/LeadParams';
import { Features, SourcePages } from 'lib/EventSchema';
import { useGeolocationData } from 'lib/hooks/useGeoLocation';

import { LeadTrackingPageSource } from 'types/Apis/LeadTrackingParams';
import {
  EnhancedLeadTrackingPageSource,
  EnhancedLeadTrackingPlatform,
} from 'types/Apis/EnhancedLeadTrackingFields';
import State from 'types/State';

import { SearchVehicle } from 'lib/apis/SearchApi/types';
import useUtmTracking from './useUtmTracking';
import useFirstVisitTracking from './useFirstVisitTracking';
import useEvents from './useEvents';
import { useSetQuickContact } from './useQuickContact';
import { useLeadFormInitialData } from './useLeadFormInitialData';
import useIsPaid from './useIsPaid';
import useSnowplowSessionId from './useSnowplowSessionId';
import useDeviceScreen from './useDeviceScreen';

interface LeadSubmitterParams {
  vehicle: SearchVehicle;
  contactInfo: ContactInfo;
  sendAlerts: boolean | undefined;
}

interface QuickLeadSubmitterParams {
  vehicle: SearchVehicle;
  sendAlerts: boolean | undefined;
}

async function postLead(params: LeadParams): Promise<Response> {
  return withOptionalAuth(autolistFetch)(`/leads`, {
    body: JSON.stringify(params),
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
}

type SourceFeature = Features.modalLeadForm | Features.leadForm | Features.quickPicks;

const fallbackGeoLocationData = {
  city: '',
  state: undefined,
  latitude: 0,
  longitude: 0,
  zip: '',
  stateAbbreviation: '',
};

export function useQuickLeadSubmitter(
  sourceFeature: SourceFeature,
  sourcePage: LeadTrackingPageSource,
  enhancedSourcePage: EnhancedLeadTrackingPageSource,
  sourcePageForm: SourcePages,
  sourcePageCategory?: string,
  adExtId?: string,
  adPartnerId?: string,
  pagePosition?: number
): (params: QuickLeadSubmitterParams) => Promise<any> {
  const geolocation = useGeolocationData();
  const firstVisitTracking = useFirstVisitTracking();
  const utmTracking = useUtmTracking();
  const dispatch = useDispatch<AppDispatch>();
  const { isMobile, isTablet } = useDeviceScreen();
  const isPaid = useIsPaid();
  const logger = useEvents();
  const autolistUser = useLeadFormInitialData({ fromAccount: true });
  const snowplowSessionId = useSnowplowSessionId();
  const platform =
    isMobile || isTablet
      ? EnhancedLeadTrackingPlatform.Mobile
      : EnhancedLeadTrackingPlatform.Desktop;
  return async function quickLeadSubmit({ vehicle, sendAlerts }): Promise<any> {
    if (autolistUser) {
      const params = new LeadParams({
        vin: vehicle.vin,
        isRegional: vehicle.isRegional,
        partnerType: vehicle.partnerType,
        trackingParams: vehicle.trackingParams,
        emailAcceptance: {
          price: sendAlerts || false,
          search: sendAlerts || false,
          marketing: sendAlerts || false,
        },
        contactInfo: autolistUser,
        geolocation: geolocation || fallbackGeoLocationData,
        firstVisitTracking,
        utmTracking,
        isPaid,
        sourcePage,
        enhancedLeadTrackingFields: {
          ad_id: adExtId,
          partner_id: adPartnerId,
          platform,
          snowplow_domain_session_id: snowplowSessionId,
          page_position: pagePosition,
          page_source: enhancedSourcePage,
        },
      });
      let response = await postLead(params);

      const sourcePageUrl = window.location.pathname;

      if (response.ok) {
        response = await response.json();

        logger.log(
          responseSuccessEvent({
            feature: sourceFeature,
            statusCode: response.status,
            trackingParams: params.trackingParams,
            sourcePage: sourcePageForm,
            sourcePageUrl,
            sourcePageCategory: sourcePageCategory || '',
            vin: vehicle.vin,
          })
        );
      } else {
        const { status, statusText, url } = response;
        logger.log(
          responseFailureEvent({
            feature: sourceFeature,
            errorCode: status,
            errorMessage: statusText,
            trackingParams: params.trackingParams,
            sourcePage: sourcePageForm,
            sourcePageUrl,
            sourcePageCategory: sourcePageCategory || '',
            vin: vehicle.vin,
          })
        );
        throw new Error(`Got status ${status} while fetching ${url}`);
      }

      dispatch<any>(logLeadSubmitted(params.toJSON()));
      return {
        leadResponse: response,
        leadPayload: params,
      };
    }
    return null;
  };
}

export function useLeadSubmitter(
  sourceFeature: SourceFeature,
  sourcePage: LeadTrackingPageSource,
  enhancedSourcePage: EnhancedLeadTrackingPageSource,
  sourcePageForm: SourcePages,
  platform: EnhancedLeadTrackingPlatform,
  pagePosition?: number,
  sourcePageCategory?: string,
  requestedPrice?: number,
  adExtId?: string,
  adPartnerId?: string
): (params: LeadSubmitterParams) => Promise<any> {
  const isPaid = useIsPaid();
  const firstVisitTracking = useFirstVisitTracking();
  const utmTracking = useUtmTracking();
  const logger = useEvents();
  const dispatch = useDispatch<AppDispatch>();
  const geolocation = useSelector((state: State) => getGeoLocation(state), shallowEqual);
  const initializeQuickContact = useSetQuickContact();
  const snowplowSessionId = useSnowplowSessionId();

  return async function leadSubmitter({
    vehicle,
    contactInfo,
    sendAlerts,
  }: LeadSubmitterParams): Promise<any> {
    saveMostRecentLeadSubmission({
      name: contactInfo.name,
      phone: contactInfo.phone,
      email: contactInfo.email,
    });

    const params = new LeadParams({
      vin: vehicle.vin,
      isRegional: vehicle.isRegional,
      partnerType: vehicle.partnerType,
      trackingParams: vehicle.trackingParams,
      emailAcceptance: {
        price: sendAlerts || false,
        search: sendAlerts || false,
        marketing: sendAlerts || false,
      },
      contactInfo,
      geolocation: geolocation || fallbackGeoLocationData,
      firstVisitTracking,
      utmTracking,
      isPaid,
      sourcePage,
      enhancedLeadTrackingFields: {
        page_source: enhancedSourcePage,
        platform,
        page_position: pagePosition,
        snowplow_domain_session_id: snowplowSessionId,
        ad_id: adExtId,
        partner_id: adPartnerId,
      },
    });

    const userInfo = await ensureSignedIn(contactInfo.email);
    if (userInfo) {
      dispatch(sessionSignedIn(userInfo));
    }

    logger.log(
      requestSentEvent({
        feature: sourceFeature,
        trackingParams: params.trackingParams,
        sourcePage: sourcePageForm,
        vin: vehicle.vin,
        price: vehicle.price,
        requestedPrice,
      })
    );

    let response = await postLead(params);

    const sourcePageUrl = window.location.pathname;

    if (response.ok) {
      response = await response.json();

      if (isSignedIn()) {
        initializeQuickContact();
      }

      logger.log(
        responseSuccessEvent({
          feature: sourceFeature,
          statusCode: response.status,
          trackingParams: params.trackingParams,
          sourcePage: sourcePageForm,
          sourcePageUrl,
          sourcePageCategory: sourcePageCategory || '',
          vin: vehicle.vin,
          price: vehicle.price,
          requestedPrice,
        })
      );
    } else {
      const { status, statusText, url } = response;
      logger.log(
        responseFailureEvent({
          feature: sourceFeature,
          errorCode: status,
          errorMessage: statusText,
          trackingParams: params.trackingParams,
          sourcePage: sourcePageForm,
          sourcePageUrl,
          sourcePageCategory: sourcePageCategory || '',
          vin: vehicle.vin,
          price: vehicle.price,
          requestedPrice,
        })
      );
      throw new Error(`Got status ${status} while fetching ${url}`);
    }

    dispatch<any>(logLeadSubmitted(params.toJSON()));

    return {
      leadResponse: response,
      leadPayload: params,
    };
  };
}
